// 'use client'

import { useCarousel } from './Carousel'
import { motion } from 'framer-motion'
import Image from 'next/image'
import { SquareArrowOutUpRight, Headset } from 'lucide-react'
import Link from 'next/link'

export type SlideContentProps = {
  image: {
    src: string
    alt: string
    title: string
    subtitle: string
    offerTitle: string
    offerDescription: string
    offerBtnText: string
    offerUrl: string
    cta1Text: string
    cta2Text: string
  }
  index: number
}

const ClockIcon = ({ className }: { className?: string }) => (
  <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
)

const SlideContent: React.FC<SlideContentProps> = ({ image, index }) => {
  const { index: activeIndex } = useCarousel()
  const isActive = index === activeIndex

  return (
    <div className="relative w-full h-[700px] lg:h-[800px] overflow-hidden group/slide bg-black">
      {/* Background Image & Overlay */}
      <div className="absolute inset-0 z-0">
        <Image
          src={image.src || "/placeholder.svg"}
          alt={image.alt}
          fill
          className="object-cover scale-100 group-hover/slide:scale-105 transition-transform duration-2000"
          sizes="100vw"
          priority={index === 0}
          fetchPriority={index === 0 ? 'high' : 'auto'}
          decoding="async"
          quality={90}
        />
        <div className="absolute inset-0 bg-linear-to-t from-black/80 via-black/30 to-transparent" />
      </div>

      {/* Content Area */}
      <div className="container mx-auto px-8 flex items-center h-full relative z-10">
        <motion.div
          className="text-white max-w-2xl mt-16"
          initial={false}
          animate={{ opacity: isActive ? 1 : 0, y: isActive ? 0 : 30 }}
          transition={{ duration: 0.6, ease: 'easeOut', delay: isActive ? 0.3 : 0 }}
        >
          {/* Sub Title */}
        {image.subtitle && (
          <motion.div
            className="font-oswaldz ml-1 w-fit rounded-sm"
            animate={{
              opacity: isActive ? 1 : 0,
              y: isActive ? 0 : -60,
            }}
            transition={{ delay: isActive ? 1.4 : 0, duration: 1 }}
          >
            <p className="brand-badge"> {image.subtitle}</p>
          </motion.div>
        )}


        

        {/* Title */}
        {image.title && (
          <motion.div
            className="text-white text-2xl md:text-4xl font-oswald font-extrabold uppercase tracking-[2] -ml-3 mt-2 mb-1 p-3 w-fit bg-gray-50/20z rounded-xs"
            animate={{
              opacity: isActive ? 1 : 0,
              x: isActive ? 0 : 60,
            }}
            transition={{ delay: isActive ? 0.4 : 0, duration: 1 }}
          >
            {image.title}
          </motion.div>
        )}

          {/* Offer Description */}
        {image.offerDescription && (
          <motion.div
            className="flex gap-1 text-xs md:text-sm opacity-90 p-0.5 h-8"
            animate={{
              opacity: isActive ? 1 : 0,
              x: isActive ? 0 : 40,
            }}
            transition={{ delay: isActive ? 0.6 : 0, duration: 1 }}
          >
            <motion.p
              className="text-black font-bold text-[10px] md:text-[11px] opacity-90 bg-white flex items-center p-2"
              animate={{
                opacity: isActive ? 1 : 0,
                x: isActive ? 0 : 40,
              }}
              transition={{ delay: isActive ? 0.6 : 0, duration: 1 }}
            >
              {image.offerDescription}
            </motion.p>
            {image.offerBtnText && (
              <Link href={image.offerUrl} className='text-black font-bold text-[11px] md:text-[11px] opacity-90 bg-accent flex items-center p-2'>{image.offerBtnText}</Link>
            )}
          </motion.div>
        )}


        {/* Terms & Conditions */}
        {image.offerTitle && image.offerDescription && (
          <motion.p
            className="text-[10px] md:text-[9px] opacity-80"
            animate={{
              opacity: isActive ? 1 : 0,
              x: isActive ? 0 : 40,
            }}
            transition={{ delay: isActive ? 0.6 : 0, duration: 1 }}
          >
            terms & conditions apply 
            <span className='text-[14px] text-accent'>*</span>
          </motion.p>
        )}

          {/* Buttons */}
        <div className="flex gap-x-4">
          {image.cta1Text && (
            <Link href={'/home'} className='font-oswald text-white text-xs md:text-base uppercase bg-primary py-2 px-5 mt-4 rounded-sm flex items-center gap-x-2'><SquareArrowOutUpRight />{image.cta1Text}</Link>
          )}
          {image.cta2Text && (
            <Link href={'/home'} className='font-oswald text-white text-xs md:text-base uppercase border border-accent py-2 px-5 mt-4 rounded-sm flex items-center gap-x-2'><Headset />{image.cta2Text}</Link>
          )}
        </div>

        </motion.div>
      </div>
    </div>
  )
}

export default SlideContent
